import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Career = () => {
  const [activeJob, setActiveJob] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const [dbJobs, setDbJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [awaitingLeadInfo, setAwaitingLeadInfo] = useState(false);
  const [leadStep, setLeadStep] = useState(0);
  const [userData, setUserData] = useState({ name: '', phone: '', email: '', position: '' });
  const [messageCount, setMessageCount] = useState(0);
  const [quickReplies, setQuickReplies] = useState([]);
  const [chatbotHover, setChatbotHover] = useState(false);

  const API_BASE_URL = 'http://localhost:5000/api';

  useEffect(() => {
    fetchJobsFromDatabase();
  }, []);

  useEffect(() => {
    if (isChatOpen && chatMessages.length === 0) {
      initChat();
    }
  }, [isChatOpen]);

  const fetchJobsFromDatabase = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/jobs/active`);
      
      if (response.data.success) {
        const mappedJobs = response.data.data.map(job => ({
          id: job._id || job.id,
          title: job.title,
          department: job.department,
          location: job.location,
          type: job.type,
          experience: job.experience,
          description: job.description,
          responsibilities: job.responsibilities || [],
          requirements: job.requirements || [],
          benefits: job.benefits || [],
          salary: job.salary || '₹ - Competitive Salary',
          isActive: job.isActive
        }));
        
        setDbJobs(mappedJobs);
        setError(null);
      } else {
        setError('Failed to fetch jobs from server');
      }
    } catch (err) {
      setError('Failed to connect to server.');
      setDbJobs(jobOpenings);
    } finally {
      setLoading(false);
    }
  };

  const jobOpenings = [
    {
      id: 1,
      title: "Solar Installation Technician",
      department: "Installation",
      location: "Mumbai, Maharashtra",
      type: "Full-time",
      experience: "2-5 years",
      description: "Join our installation team to deploy residential and commercial solar systems.",
      responsibilities: [
        "Install solar panels, inverters, and mounting systems",
        "Perform system testing and commissioning",
        "Ensure compliance with safety standards",
        "Provide basic customer training on system operation"
      ],
      requirements: [
        "ITI/Diploma in Electrical Engineering",
        "Experience with electrical wiring and DC systems",
        "Knowledge of solar installation best practices",
        "Valid driver's license"
      ],
      benefits: ["Health insurance", "Performance bonus", "Training programs", "Career growth"],
      salary: "₹3-5 LPA + incentives"
    },
    {
      id: 2,
      title: "Solar Sales Executive",
      department: "Sales & Marketing",
      location: "Pune, Maharashtra",
      type: "Full-time",
      experience: "1-3 years",
      description: "Drive solar adoption by helping homeowners and businesses understand solar benefits.",
      responsibilities: [
        "Generate leads through various channels",
        "Conduct site surveys and energy assessments",
        "Prepare and present solar proposals",
        "Build and maintain customer relationships"
      ],
      requirements: [
        "Degree in any field",
        "Excellent communication skills",
        "Experience in B2C or B2B sales",
        "Basic understanding of solar technology"
      ],
      benefits: ["High commission structure", "Health insurance", "Sales training", "Travel allowance"],
      salary: "₹2.5-4 LPA + commissions"
    }
  ];

  const allJobs = dbJobs.length > 0 ? dbJobs : jobOpenings;

  const culturePoints = [
    {
      icon: "🌱",
      title: "Sustainable Impact",
      description: "Every project you work on directly contributes to reducing carbon emissions and promoting clean energy."
    },
    {
      icon: "🚀",
      title: "Growth Opportunities",
      description: "We invest in continuous training and development to help you grow professionally in the renewable energy sector."
    },
    {
      icon: "🤝",
      title: "Collaborative Environment",
      description: "Work with passionate professionals who share your commitment to building a sustainable future."
    },
    {
      icon: "⚖️",
      title: "Work-Life Balance",
      description: "Flexible hours and hybrid work options to ensure you maintain a healthy work-life balance."
    }
  ];

  const employeeBenefits = [
    {
      title: "Competitive Compensation",
      items: ["Above-market salaries", "Performance bonuses", "Incentive programs"]
    },
    {
      title: "Health & Wellness",
      items: ["Health insurance", "Mental health support", "Gym memberships", "Annual health checkups"]
    },
    {
      title: "Learning & Development",
      items: ["Training programs", "Certification support", "Conference attendance", "Skill development"]
    },
    {
      title: "Work Environment",
      items: ["Modern office spaces", "Hybrid work options", "Team outings", "Celebrations & events"]
    }
  ];

  const applicationSteps = [
    {
      number: "01",
      title: "Apply Online",
      description: "Submit your application through our portal"
    },
    {
      number: "02",
      title: "Initial Screening",
      description: "Phone/video interview with HR"
    },
    {
      number: "03",
      title: "Technical Assessment",
      description: "Skills evaluation & practical test"
    },
    {
      number: "04",
      title: "Final Interview",
      description: "Meet the team and department head"
    },
    {
      number: "05",
      title: "Offer & Onboarding",
      description: "Welcome to Blue Sky Energy!"
    }
  ];

  const initChat = () => {
    addChatMessage("Hi 👋 Welcome to Blue Sky Energy Careers! I'm here to help you with job applications and career opportunities. How can I assist you today?", 'bot');
    setTimeout(() => {
      addChatMessage("Choose an option to get started:", 'bot');
      setQuickReplies([
        "💼 Current Job Openings", 
        "📝 Apply for Position", 
        "📞 HR Contact",
        "🎯 Career Growth",
        "💰 Salary & Benefits"
      ]);
    }, 800);
  };

  const addChatMessage = (text, sender) => {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const newMessage = {
      text,
      sender,
      time,
      id: Date.now()
    };
    setChatMessages(prev => [...prev, newMessage]);
    if (sender === 'user') {
      setMessageCount(prev => prev + 1);
    }
  };

  const matchKeywords = (input) => {
    const text = input.toLowerCase();
    
    if (text.includes('job') || text.includes('opening') || text.includes('vacancy') || text.includes('position')) {
      let jobList = "💼 Current Job Openings\n\n";
      allJobs.forEach((job, index) => {
        jobList += `${index + 1}. ${job.title} - ${job.location}\n`;
      });
      jobList += "\nWhich position interests you?";
      return jobList;
    }
    
    if (text.includes('apply') || text.includes('application') || text.includes('resume') || text.includes('cv')) {
      return "📝 Application Process\n\n1. Submit application online\n2. HR screening interview\n3. Technical assessment\n4. Final interview\n5. Offer & onboarding\n\nAverage process: 2-3 weeks";
    }
    
    if (text.includes('salary') || text.includes('pay') || text.includes('compensation') || text.includes('benefit')) {
      return "💰 Salary & Benefits\n\n• Competitive above-market salaries\n• Performance bonuses\n• Health insurance\n• Training & certification support\n• Flexible work options";
    }
    
    if (text.includes('contact') || text.includes('hr') || text.includes('human resource')) {
      return "📞 HR Contact\n\n• Email: careers@blueskyenergy.in\n• Phone: +91 80879 32436\n• Response time: 24-48 hours";
    }
    
    return `We have ${allJobs.length} open positions. What would you like to know more about?`;
  };

  const handleLeadCapture = (input) => {
    if (leadStep === 0) {
      setUserData(prev => ({ ...prev, name: input }));
      setLeadStep(1);
      addChatMessage(`Nice to meet you, ${input}! 😊\n\nWhich position are you interested in?`, 'bot');
    } else if (leadStep === 1) {
      setUserData(prev => ({ ...prev, position: input }));
      setLeadStep(2);
      addChatMessage("Great! Could you please share your email address?", 'bot');
    } else if (leadStep === 2) {
      setUserData(prev => ({ ...prev, email: input }));
      setLeadStep(3);
      addChatMessage("Perfect! And what's your phone number?", 'bot');
    } else if (leadStep === 3) {
      setUserData(prev => ({ ...prev, phone: input }));
      setLeadCaptured(true);
      setAwaitingLeadInfo(false);
      const applicationId = `career_app_${Date.now()}`;
      localStorage.setItem(applicationId, JSON.stringify({
        ...userData, 
        phone: input,
        timestamp: new Date(), 
        source: 'career_chatbot'
      }));
      addChatMessage(`Excellent! Thank you ${userData.name}! ✅\n\nOur HR team will contact you within 48 hours. Good luck! 🍀`, 'bot');
    }
  };

  const handleChatSend = () => {
    const text = chatInput.trim();
    if (!text) return;

    addChatMessage(text, 'user');
    setChatInput('');
    setQuickReplies([]);

    setTimeout(() => {
      if (awaitingLeadInfo) {
        handleLeadCapture(text);
      } else {
        const response = matchKeywords(text);
        addChatMessage(response, 'bot');

        if (!leadCaptured && messageCount >= 1) {
          setTimeout(() => {
            addChatMessage("Ready to apply? 📋\n\nWhat's your name?", 'bot');
            setAwaitingLeadInfo(true);
          }, 1500);
        }
      }
    }, 600);
  };

  const handleQuickReply = (option) => {
    setChatInput(option);
    setTimeout(() => {
      handleChatSend();
    }, 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleChatSend();
    }
  };

  const filteredJobs = activeCategory === 'all' 
    ? allJobs 
    : allJobs.filter(job => job.department === activeCategory);

  const departments = ['all', 'Installation', 'Sales & Marketing', 'Engineering', 'Customer Service', 'Operations'];

  return (
    <>
      <style>
        {`
          /* Color Palette */
          :root {
            --color-deep-teal: #061221;
            --color-dark-navy: #123249;
            --color-midnight: #2D5A74;
            --color-slate: #447793;
            --color-frost: #DDE8ED;
            --color-white: #ffffff;
            --gradient-primary: linear-gradient(135deg, #447793 0%, #2D5A74 100%);
            --gradient-hover: linear-gradient(135deg, #2D5A74 0%, #123249 100%);
            --gradient-light: linear-gradient(135deg, #DDE8ED 0%, #ffffff 100%);
            --shadow-sm: 0 2px 15px rgba(6, 18, 33, 0.08);
            --shadow-md: 0 4px 20px rgba(6, 18, 33, 0.12);
            --shadow-lg: 0 10px 30px rgba(6, 18, 33, 0.15);
          }

          /* Responsive CSS */
          @media (max-width: 1200px) {
            .main-content {
              padding: 50px 20px !important;
            }
          }
          
          @media (max-width: 992px) {
            .hero-title {
              font-size: 2.5rem !important;
            }
            
            .hero-subtitle {
              font-size: 1.1rem !important;
            }
            
            .section-title {
              font-size: 2rem !important;
            }
            
            .section-subtitle {
              font-size: 1rem !important;
            }
            
            .process-steps {
              flex-wrap: wrap !important;
              gap: 30px !important;
            }
            
            .process-step {
              flex: 0 0 calc(33.333% - 20px) !important;
              margin-bottom: 30px !important;
            }
            
            .connecting-line {
              display: none !important;
            }
          }
          
          @media (max-width: 768px) {
            .hero-section {
              padding: 80px 15px 50px !important;
            }
            
            .hero-title {
              font-size: 2rem !important;
            }
            
            .hero-subtitle {
              font-size: 1rem !important;
              padding: 0 10px !important;
            }
            
            .culture-grid,
            .benefits-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 20px !important;
            }
            
            .category-filter {
              flex-wrap: wrap !important;
              gap: 10px !important;
              justify-content: center !important;
            }
            
            .category-button {
              padding: 8px 16px !important;
              font-size: 14px !important;
            }
            
            .job-header {
              flex-direction: column !important;
              gap: 15px !important;
              align-items: flex-start !important;
            }
            
            .job-meta {
              flex-wrap: wrap !important;
              gap: 10px !important;
            }
            
            .process-step {
              flex: 0 0 calc(50% - 15px) !important;
            }
            
            .contact-info {
              flex-direction: column !important;
              gap: 20px !important;
              align-items: center !important;
            }
            
            .cta-buttons {
              flex-direction: column !important;
              gap: 15px !important;
            }
            
            .cta-button {
              width: 100% !important;
              text-align: center;
            }
            
            /* Chatbot Mobile */
            .chat-window {
              width: 90vw !important;
              right: 5vw !important;
              left: 5vw !important;
              height: 500px !important;
              max-height: 70vh !important;
            }
            
            .chat-message {
              max-width: 85% !important;
              font-size: 13px !important;
            }
            
            .quick-replies {
              padding: 10px !important;
            }
            
            .reply-btn {
              font-size: 12px !important;
              padding: 6px 12px !important;
            }
            
            .chat-input {
              font-size: 13px !important;
              padding: 10px 14px !important;
            }
            
            .send-btn {
              width: 40px !important;
              height: 40px !important;
              font-size: 14px !important;
            }
          }
          
          @media (max-width: 576px) {
            .hero-title {
              font-size: 1.8rem !important;
            }
            
            .hero-subtitle {
              font-size: 0.95rem !important;
            }
            
            .section-title {
              font-size: 1.6rem !important;
            }
            
            .culture-grid,
            .benefits-grid {
              grid-template-columns: 1fr !important;
            }
            
            .process-step {
              flex: 0 0 100% !important;
            }
            
            .job-meta-item {
              flex: 0 0 100% !important;
            }
            
            .chat-window {
              width: 95vw !important;
              right: 2.5vw !important;
              left: 2.5vw !important;
              height: 450px !important;
            }
            
            .job-card,
            .culture-card,
            .benefit-card {
              padding: 20px !important;
            }
            
            .why-join-section,
            .jobs-section,
            .benefits-section,
            .process-section,
            .cta-section {
              padding: 40px 15px !important;
            }
          }
          
          @media (max-width: 480px) {
            .hero-section {
              padding: 60px 10px 40px !important;
            }
            
            .hero-title {
              font-size: 1.5rem !important;
              margin-bottom: 15px !important;
            }
            
            .cta-title {
              font-size: 1.8rem !important;
            }
            
            .job-title {
              font-size: 1.1rem !important;
            }
            
            .details-title {
              font-size: 1rem !important;
            }
            
            .step-title {
              font-size: 1rem !important;
            }
            
            .footer-text {
              font-size: 10px !important;
            }
          }
          
          /* Touch-friendly styles */
          @media (hover: none) and (pointer: coarse) {
            .job-card:hover,
            .culture-card:hover,
            .category-button:hover,
            .apply-button:hover,
            .cta-button:hover {
              transform: none !important;
            }
          }
          
          /* Prevent zoom on mobile input */
          @media (max-width: 768px) {
            input, textarea, select {
              font-size: 16px !important;
            }
          }
          
          /* Smooth transitions */
          .job-card,
          .culture-card,
          .benefit-card,
          .apply-button,
          .cta-button {
            transition: all 0.3s ease;
          }
          
          /* Job meta responsive */
          .job-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 15px;
          }
          
          @media (max-width: 768px) {
            .job-meta {
              gap: 10px;
            }
          }
          
          @media (max-width: 480px) {
            .job-meta {
              gap: 8px;
            }
            
            .job-meta-item {
              flex: 0 0 calc(50% - 4px) !important;
              font-size: 12px !important;
            }
          }
        `}
      </style>

      <div style={{
        fontFamily: "Cambria, serif",
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        color: '#123249',
        overflowX: 'hidden'
      }}>
        {/* Hero Section - Blue Theme */}
        <section className="hero-section" style={{
          background: `linear-gradient(rgba(18, 50, 73, 0.95), rgba(45, 90, 116, 0.95)), url('https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-1.2.1&auto=format&fit=crop&w=1600&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          padding: '100px 20px 60px',
          textAlign: 'center',
          position: 'relative'
        }}>
          <h1 className="hero-title" style={{
            fontSize: '3rem',
            fontWeight: '800',
            marginBottom: '20px',
            textShadow: '2px 2px 4px rgba(6, 18, 33, 0.3)'
          }}>
            Join Our Solar Revolution
          </h1>
          <p className="hero-subtitle" style={{
            fontSize: '1.2rem',
            maxWidth: '800px',
            margin: '0 auto 40px',
            opacity: '0.95',
            lineHeight: '1.6',
            color: '#DDE8ED'
          }}>
            Build your career while building a sustainable future. Join Blue Sky Energy and be part of India's clean energy transformation.
          </p>
          <a href="#job-openings" style={{
            display: 'inline-block',
            backgroundColor: 'white',
            color: '#447793',
            padding: '12px 30px',
            borderRadius: '30px',
            fontWeight: '700',
            fontSize: '1rem',
            textDecoration: 'none',
            transition: 'all 0.3s ease',
            boxShadow: '0 10px 30px rgba(6, 18, 33, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#DDE8ED';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'white';
            e.target.style.transform = 'none';
          }}
          >
            View Open Positions →
          </a>
        </section>

        {/* Why Join Us Section - Blue Theme */}
        <section className="why-join-section" style={{
          padding: '60px 20px',
          backgroundColor: '#DDE8ED'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 className="section-title" style={{
                fontSize: '2.2rem',
                fontWeight: '800',
                color: '#123249',
                marginBottom: '15px'
              }}>
                Why Join Blue Sky Energy?
              </h2>
              <p className="section-subtitle" style={{
                fontSize: '1.1rem',
                color: '#2D5A74',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: '1.6'
              }}>
                We're not just another company - we're a movement.
              </p>
            </div>
            
            <div className="culture-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '25px',
              marginTop: '30px'
            }}>
              {culturePoints.map((point, index) => (
                <div 
                  key={index} 
                  className="culture-card"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '15px',
                    padding: '25px',
                    textAlign: 'center',
                    boxShadow: '0 10px 25px rgba(6, 18, 33, 0.05)',
                    transition: 'all 0.3s ease',
                    border: '1px solid #DDE8ED'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(68, 119, 147, 0.1)';
                    e.currentTarget.style.borderColor = '#447793';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(6, 18, 33, 0.05)';
                    e.currentTarget.style.borderColor = '#DDE8ED';
                  }}
                >
                  <div style={{ fontSize: '2.5rem', marginBottom: '20px' }}>{point.icon}</div>
                  <h3 style={{
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    color: '#123249',
                    marginBottom: '15px'
                  }}>{point.title}</h3>
                  <p style={{
                    color: '#2D5A74',
                    fontSize: '0.95rem',
                    lineHeight: '1.6'
                  }}>{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Current Openings Section - Blue Theme */}
        <section className="jobs-section" id="job-openings" style={{
          padding: '60px 20px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 className="section-title" style={{
                fontSize: '2.2rem',
                fontWeight: '800',
                color: '#123249',
                marginBottom: '15px'
              }}>
                Current Openings
              </h2>
              <p className="section-subtitle" style={{
                fontSize: '1.1rem',
                color: '#2D5A74',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: '1.6'
              }}>
                Explore opportunities to grow your career in renewable energy.
              </p>
            </div>

            {loading && (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: '#DDE8ED',
                borderRadius: '10px',
                margin: '20px 0'
              }}>
                <h3 style={{color: '#123249'}}>Loading job openings...</h3>
              </div>
            )}

            {error && !loading && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                backgroundColor: '#DDE8ED',
                color: '#123249',
                borderRadius: '10px',
                margin: '20px 0'
              }}>
                <p>{error}</p>
                <p style={{ marginTop: '10px' }}>Showing sample job openings.</p>
              </div>
            )}

            {/* Category Filter */}
            <div className="category-filter" style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '15px',
              marginBottom: '30px'
            }}>
              {departments.map(dept => (
                <button
                  key={dept}
                  onClick={() => setActiveCategory(dept)}
                  className="category-button"
                  style={{
                    padding: '10px 20px',
                    borderRadius: '25px',
                    border: '2px solid #447793',
                    backgroundColor: activeCategory === dept ? '#447793' : 'white',
                    color: activeCategory === dept ? 'white' : '#447793',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => {
                    if (activeCategory !== dept) {
                      e.target.style.backgroundColor = '#DDE8ED';
                      e.target.style.borderColor = '#2D5A74';
                      e.target.style.color = '#123249';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeCategory !== dept) {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.borderColor = '#447793';
                      e.target.style.color = '#447793';
                    }
                  }}
                >
                  {dept === 'all' ? 'All Departments' : dept}
                </button>
              ))}
            </div>

            {/* Job Listings */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              {filteredJobs.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  backgroundColor: '#DDE8ED',
                  borderRadius: '10px',
                  margin: '20px 0'
                }}>
                  <h3 style={{color: '#123249'}}>No job openings found</h3>
                  <p style={{color: '#2D5A74'}}>Check back later for new opportunities.</p>
                </div>
              ) : (
                filteredJobs.map((job, index) => (
                  <div key={job.id || index}>
                    <div 
                      className="job-card"
                      style={{
                        backgroundColor: '#DDE8ED',
                        borderRadius: '12px',
                        padding: '20px',
                        cursor: 'pointer',
                        border: '2px solid transparent',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => setActiveJob(activeJob === job.id ? 0 : job.id)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#447793';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(68, 119, 147, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div className="job-header" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '15px'
                      }}>
                        <div>
                          <h3 className="job-title" style={{
                            fontSize: '1.2rem',
                            fontWeight: '700',
                            color: '#123249',
                            marginBottom: '10px'
                          }}>{job.title}</h3>
                          <div className="job-meta">
                            <span className="job-meta-item" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              color: '#2D5A74',
                              fontSize: '0.9rem'
                            }}>📍 {job.location}</span>
                            <span className="job-meta-item" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              color: '#2D5A74',
                              fontSize: '0.9rem'
                            }}>⏰ {job.type}</span>
                            <span className="job-meta-item" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              color: '#2D5A74',
                              fontSize: '0.9rem'
                            }}>📅 {job.experience}</span>
                          </div>
                        </div>
                        <button className="apply-button" style={{
                          backgroundColor: '#447793',
                          color: 'white',
                          border: 'none',
                          padding: '8px 20px',
                          borderRadius: '25px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#2D5A74';
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 5px 15px rgba(45, 90, 116, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#447793';
                          e.target.style.transform = 'none';
                          e.target.style.boxShadow = 'none';
                        }}
                        >
                          Apply Now
                        </button>
                      </div>
                      <p style={{color: '#2D5A74', fontSize: '0.95rem'}}>{job.description}</p>
                    </div>

                    {activeJob === job.id && (
                      <div style={{
                        marginTop: '15px',
                        padding: '20px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #DDE8ED',
                        boxShadow: '0 5px 15px rgba(6, 18, 33, 0.05)'
                      }}>
                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            color: '#123249',
                            marginBottom: '15px'
                          }}>Key Responsibilities</h4>
                          <div>
                            {job.responsibilities.map((item, idx) => (
                              <div key={idx} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                marginBottom: '10px',
                                color: '#2D5A74',
                                fontSize: '0.95rem'
                              }}>
                                <span style={{color: '#447793', marginTop: '3px'}}>•</span>
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            color: '#123249',
                            marginBottom: '15px'
                          }}>Requirements</h4>
                          <div>
                            {job.requirements.map((item, idx) => (
                              <div key={idx} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                marginBottom: '10px',
                                color: '#2D5A74',
                                fontSize: '0.95rem'
                              }}>
                                <span style={{color: '#447793', marginTop: '3px'}}>✓</span>
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            color: '#123249',
                            marginBottom: '15px'
                          }}>Benefits</h4>
                          <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                            {job.benefits.map((benefit, idx) => (
                              <span key={idx} style={{
                                backgroundColor: '#DDE8ED',
                                color: '#123249',
                                padding: '6px 12px',
                                borderRadius: '15px',
                                fontSize: '0.85rem'
                              }}>
                                {benefit}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 style={{
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            color: '#123249',
                            marginBottom: '15px'
                          }}>Compensation</h4>
                          <div style={{
                            display: 'inline-block',
                            backgroundColor: '#DDE8ED',
                            color: '#123249',
                            padding: '8px 20px',
                            borderRadius: '20px',
                            fontWeight: '700',
                            fontSize: '0.95rem'
                          }}>
                            💰 {job.salary}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Employee Benefits Section - Blue Theme */}
        <section className="benefits-section" style={{
          padding: '60px 20px',
          backgroundColor: '#DDE8ED'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 className="section-title" style={{
                fontSize: '2.2rem',
                fontWeight: '800',
                color: '#123249',
                marginBottom: '15px'
              }}>
                Employee Benefits & Perks
              </h2>
              <p className="section-subtitle" style={{
                fontSize: '1.1rem',
                color: '#2D5A74',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: '1.6'
              }}>
                We believe in taking care of our team.
              </p>
            </div>
            
            <div className="benefits-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '25px',
              marginTop: '30px'
            }}>
              {employeeBenefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className="benefit-card"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '15px',
                    padding: '25px',
                    boxShadow: '0 10px 25px rgba(6, 18, 33, 0.05)',
                    border: '1px solid #DDE8ED'
                  }}
                >
                  <h3 style={{
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    color: '#123249',
                    marginBottom: '20px'
                  }}>{benefit.title}</h3>
                  <ul style={{ listStyle: 'none', padding: '0' }}>
                    {benefit.items.map((item, idx) => (
                      <li key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '12px',
                        color: '#2D5A74',
                        fontSize: '0.95rem'
                      }}>
                        <span style={{color: '#447793'}}>✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Process Section - Blue Theme */}
        <section className="process-section" style={{
          padding: '60px 20px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 className="section-title" style={{
                fontSize: '2.2rem',
                fontWeight: '800',
                color: '#123249',
                marginBottom: '15px'
              }}>
                Our Hiring Process
              </h2>
              <p className="section-subtitle" style={{
                fontSize: '1.1rem',
                color: '#2D5A74',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: '1.6'
              }}>
                A smooth and transparent experience for all candidates.
              </p>
            </div>
            
            <div className="process-steps" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginTop: '40px',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '30px',
                left: '10%',
                right: '10%',
                height: '3px',
                backgroundColor: '#DDE8ED',
                zIndex: '-1'
              }}></div>
              {applicationSteps.map((step, index) => (
                <div 
                  key={index} 
                  className="process-step"
                  style={{
                    textAlign: 'center',
                    flex: '1',
                    padding: '0 10px',
                    position: 'relative'
                  }}
                >
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#447793',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 15px',
                    fontSize: '1.2rem',
                    color: 'white',
                    fontWeight: '700',
                    boxShadow: '0 5px 15px rgba(68, 119, 147, 0.2)'
                  }}>
                    {step.number}
                  </div>
                  <h3 className="step-title" style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: '#123249',
                    marginBottom: '10px'
                  }}>{step.title}</h3>
                  <p className="step-description" style={{
                    color: '#2D5A74',
                    fontSize: '0.9rem',
                    lineHeight: '1.5'
                  }}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section - Blue Theme */}
        <section className="cta-section" style={{
          padding: '60px 20px',
          background: `linear-gradient(rgba(18, 50, 73, 0.95), rgba(45, 90, 116, 0.95))`,
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <h2 className="cta-title" style={{
              fontSize: '2.2rem',
              fontWeight: '800',
              marginBottom: '20px',
              color: 'white'
            }}>
              Ready to Power Your Career?
            </h2>
            <p className="cta-subtitle" style={{
              fontSize: '1.1rem',
              maxWidth: '800px',
              margin: '0 auto 40px',
              opacity: '0.95',
              color: '#DDE8ED'
            }}>
              Don't see the perfect role? We're always looking for talented individuals.
            </p>
            
            <div className="cta-buttons" style={{display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap'}}>
              <button className="cta-button" style={{
                backgroundColor: 'white',
                color: '#447793',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '30px',
                fontWeight: '700',
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#DDE8ED';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 10px 25px rgba(221, 232, 237, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'none';
                e.target.style.boxShadow = 'none';
              }}
              >
                Send Your Resume →
              </button>
              <button className="cta-button" style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                padding: '12px 30px',
                borderRadius: '30px',
                fontWeight: '700',
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = '#447793';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'white';
                e.target.style.transform = 'none';
              }}
              >
                Contact HR Team
              </button>
            </div>
            
            <div className="contact-info" style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '30px',
              flexWrap: 'wrap',
              marginTop: '40px'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1rem', color: '#DDE8ED'}}>
                <span>📧</span>
                <span>careers@blueskyenergy.in</span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1rem', color: '#DDE8ED'}}>
                <span>📞</span>
                <span>+91 80879 32436</span>
              </div>
            </div>
          </div>
        </section>

        {/* Chatbot Component - Blue Theme */}
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: '9999'
        }}>
          {/* Chat Toggle Button */}
          <button 
            className="chat-toggle-btn"
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              width: '55px',
              height: '55px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #447793, #2D5A74)',
              color: 'white',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              boxShadow: '0 8px 30px rgba(68, 119, 147, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '10000',
              padding: '0',
              transition: 'all 0.3s ease',
              animation: chatbotHover ? 'pulse 0.5s infinite' : 'none'
            }}
            onClick={() => setIsChatOpen(!isChatOpen)}
            onMouseEnter={() => setChatbotHover(true)}
            onMouseLeave={() => setChatbotHover(false)}
          >
            💬
          </button>

          {/* Chat Window */}
          <div className="chat-window" style={{
            position: 'fixed',
            bottom: '85px',
            right: '20px',
            width: '350px',
            height: '500px',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(6, 18, 33, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: '9999',
            transition: 'all 0.3s ease',
            opacity: isChatOpen ? '1' : '0',
            visibility: isChatOpen ? 'visible' : 'hidden',
            transform: isChatOpen ? 'translateY(0)' : 'translateY(20px)',
            border: '1px solid #DDE8ED'
          }}>
            {/* Chat Header */}
            <div style={{
              background: 'linear-gradient(135deg, #447793, #2D5A74)',
              color: 'white',
              padding: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  padding: '8px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  fontSize: '16px'
                }}>
                  💼
                </div>
                <div>
                  <h3 style={{ margin: '0', fontSize: '15px', fontWeight: '600', color: 'white' }}>
                    Career Assistant
                  </h3>
                  <p style={{ margin: '0', fontSize: '11px', opacity: '0.9', color: '#DDE8ED' }}>
                    Job Applications & Queries
                  </p>
                </div>
              </div>
              <button 
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '5px'
                }}
                onClick={() => setIsChatOpen(false)}
              >
                ✕
              </button>
            </div>

            {/* Quick Replies */}
            {quickReplies.length > 0 && (
              <div className="quick-replies" style={{
                padding: '12px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                background: '#DDE8ED',
                borderBottom: '1px solid #DDE8ED'
              }}>
                {quickReplies.map((option, index) => (
                  <button
                    key={index}
                    className="reply-btn"
                    style={{
                      background: 'white',
                      border: '1px solid #447793',
                      color: '#123249',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      fontWeight: '500'
                    }}
                    onClick={() => handleQuickReply(option)}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#447793';
                      e.target.style.color = 'white';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.color = '#123249';
                      e.target.style.transform = 'none';
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

            {/* Chat Messages */}
            <div className="chat-messages" style={{
              flex: '1',
              padding: '15px',
              background: '#DDE8ED',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              {chatMessages.map((msg) => (
                <div 
                  key={msg.id} 
                  className="chat-message"
                  style={{
                    maxWidth: '80%',
                    padding: '10px 14px',
                    borderRadius: '18px',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-line',
                    wordWrap: 'break-word',
                    position: 'relative',
                    ...(msg.sender === 'bot' ? {
                      background: 'white',
                      alignSelf: 'flex-start',
                      borderBottomLeftRadius: '4px',
                      boxShadow: '0 2px 8px rgba(6, 18, 33, 0.08)',
                      color: '#123249',
                      border: '1px solid #DDE8ED'
                    } : {
                      background: 'linear-gradient(135deg, #447793, #2D5A74)',
                      color: 'white',
                      alignSelf: 'flex-end',
                      borderBottomRightRadius: '4px'
                    })
                  }}
                >
                  {msg.text}
                  <span style={{
                    fontSize: '11px',
                    marginTop: '6px',
                    display: 'block',
                    opacity: '0.7'
                  }}>
                    {msg.time}
                  </span>
                </div>
              ))}
            </div>

            {/* Chat Input */}
            <div style={{
              padding: '15px',
              borderTop: '1px solid #DDE8ED',
              background: 'white'
            }}>
              <div style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '8px'
              }}>
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about jobs, salaries..."
                  className="chat-input"
                  style={{
                    flex: '1',
                    padding: '12px 16px',
                    border: '1px solid #DDE8ED',
                    borderRadius: '25px',
                    outline: 'none',
                    fontSize: '14px',
                    fontFamily: "Cambria, serif",
                    color: '#123249'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#447793'}
                  onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
                />
                <button 
                  className="send-btn"
                  style={{
                    background: 'linear-gradient(135deg, #447793, #2D5A74)',
                    color: 'white',
                    border: 'none',
                    width: '45px',
                    height: '45px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={handleChatSend}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #2D5A74, #123249)';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #447793, #2D5A74)';
                    e.target.style.transform = 'none';
                  }}
                >
                  📤
                </button>
              </div>
              <p className="footer-text" style={{
                textAlign: 'center',
                fontSize: '11px',
                color: '#2D5A74',
                margin: '0'
              }}>
                {allJobs.length} Open Positions • Growing Fast
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Career;